<template>
  <v-row no-gutters>
    <v-col>
      <div
        :style="{
          'background-size': 20 / scale + 'px',
          'background-color': this.tested
            ? this.valid
              ? 'rgba(23,184,5,0.13)'
              : 'rgba(184,41,2,0.11)'
            : '#fff',
        }"
        @click="addPoint($event, false)"
        @mousemove="drawing"
        id="drawer-pad"
      ></div>
      <v-snackbar
        :timeout="-1"
        class="draw-finish-info-snackbar"
        color="orange darken-3"
        v-model="working"
      >
        {{ $t("drawer.finish.message") }}
      </v-snackbar>
    </v-col>
    <v-col>
      <v-subheader>{{ $t("drawer.section.drawing") }}</v-subheader>
      <v-text-field
        :disabled="working || !canDraw"
        :label="$t('drawer.accuracy')"
        suffix="cm"
        type="number"
        v-model="accuracy"
      >
        <template v-slot:prepend>
          <v-icon>mdi-magnify-plus</v-icon>
        </template>
      </v-text-field>
      <v-select
        :disabled="working || !canDraw"
        :items="[0.5, 1, 2, 4, 5, 6]"
        :label="$t('drawer.scale')"
        v-model="scale"
      >
        <template v-slot:prepend>
          <v-icon>mdi-relative-scale</v-icon>
        </template>
      </v-select>
      <v-btn
        :disabled="!working || points.length < 2"
        @click="removeLastPoint"
        small
        class="mx-2"
        color="teal darken-2"
      >
        <span style="color: white">{{ $t("drawer.undo") }}</span>
        <v-icon color="white" dark right>mdi-undo</v-icon>
      </v-btn>
      <v-btn
        :disabled="!working || points.length < 3"
        @click="finishAutomatically"
        small
        class="mx-2"
        color="cyan darken-2"
      >
        <span style="color: white">{{ $t("drawer.finishAutomatically") }}</span>
        <v-icon color="white" dark right
          >mdi-checkbox-marked-circle-outline</v-icon
        >
      </v-btn>
      <v-btn
        :disabled="canDraw"
        @click="remove"
        small
        class="mx-2"
        color="orange darken-3"
      >
        <span style="color: white">{{ $t("drawer.clear") }}</span>
        <v-icon color="white" dark right>mdi-delete-forever</v-icon>
      </v-btn>
      <v-subheader>{{ $t("drawer.section.arranging") }}</v-subheader>
      <v-select
        :items="directions"
        :label="$t('steps.surface.direction')"
        item-value="value"
        v-model="entrance"
      >
        <template slot="selection" slot-scope="data">
          <v-icon>{{ data.item.icon }}</v-icon>
          {{ data.item.label }}
          <img
            :class="{
              'plank-ico-vertical': !data.item.horizontal,
              'plank-ico-horizontal': data.item.horizontal,
            }"
            src="/assets/plankico.png"
          />
        </template>
        <template slot="item" slot-scope="data">
          <v-icon>{{ data.item.icon }}</v-icon>
          {{ data.item.label }}
          <img
            :class="{
              'plank-ico-vertical': !data.item.horizontal,
              'plank-ico-horizontal': data.item.horizontal,
            }"
            src="/assets/plankico.png"
          />
        </template>
      </v-select>
      <v-select
        :items="planks"
        :label="$t('steps.materials.select.planks')"
        item-text="name"
        item-value="id"
        v-model="plank"
      >
        <template slot="selection" slot-scope="data">
          <v-icon :color="data.item.color">mdi-brightness-1</v-icon>
          {{ data.item.name }}
        </template>
        <template slot="item" slot-scope="data">
          <v-icon :color="data.item.color">mdi-brightness-1</v-icon>
          {{ data.item.name }}
        </template>
      </v-select>

      <v-select
        :items="joists"
        :label="$t('steps.materials.select.joist')"
        item-text="name"
        item-value="id"
        v-model="joist"
      ></v-select>
      <!-- Slats -->
      <v-select
        :items="slats"
        :label="$t('steps.materials.select.slats')"
        item-text="name"
        item-value="id"
        v-model="slat"
      ></v-select>

      <v-select
        :item-text="'name.' + $locale"
        :items="surfaces"
        :label="$t('steps.materials.select.surface')"
        item-value="id"
        v-model="surface"
      ></v-select>
      <v-text-field
        :label="$t('steps.materials.'+system+'.height')"
        suffix="cm"
        type="number"
        v-model="height"
      >
        <template v-slot:prepend>
          <v-icon>mdi-arrow-expand-vertical</v-icon>
        </template>
      </v-text-field>
      <v-select
        :items="algorithms"
        :label="$t('steps.materials.select.algorithm')"
        item-text="label"
        item-value="value"
        v-model="algorithm"
      ></v-select>
      <div v-if="this.tested">
        <span v-if="this.valid">
          <v-icon color="primary">mdi-check-circle-outline</v-icon>
        </span>
        <span v-else>
          <v-icon color="red">mdi-alert-decagram-outline</v-icon>
          <div style="color: #666; font-size: 12px">
            {{ $t("drawer.incorrect") }}
          </div>
        </span>
      </div>
      <v-progress-circular indeterminate v-if="loading"></v-progress-circular>
    </v-col>
  </v-row>
</template>

<script>
import "svg.js";
import _ from "lodash";

export default {
  computed: {
    pad() {
      return document.getElementById("drawer-pad");
    },
    working() {
      return this.points.length > 0 && this.canDraw;
    },
    gridSize() {
      return this.accuracy / this.scale;
    },
    surfaces() {
      return this.$store.state.materials.surfaces;
    },
    joists() {
      return this.$store.state.materials.joists;
    },
    planks() {
      return this.$store.state.materials.planks;
    },
    ////// slats
    slats() {
      return this.$store.state.materials.slats;
    },
    currentStep(){
        return this.$store.state.currentStep;
    },
    system(){
        return this.$store.state.system;
    },
    valid: {
      get() {
        return this.$store.getters.surface(this.index).valid;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "valid",
          value,
        });
      },
    },
    tested: {
      get() {
        return this.$store.getters.surface(this.index).tested;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "tested",
          value,
        });
      },
    },
    algorithm: {
      get() {
        return this.$store.getters.surface(this.index).algorithm;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "algorithm",
          value,
        });
      },
    },
    surface: {
      get() {
        return this.$store.getters.surface(this.index).surface;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "surface",
          value,
        });
      },
    },
    joist: {
      get() {
        return this.$store.getters.surface(this.index).joist;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "joist",
          value,
        });
      },
    },
    ///Slat
    slat: {
      get() {
        return this.$store.getters.surface(this.index).slat;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "slat",
          value,
        });
      },
    },

    plank: {
      get() {
        return this.$store.getters.surface(this.index).plank;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "plank",
          value,
        });
      },
    },
    entrance: {
      get() {
        return this.$store.getters.surface(this.index).entrance;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "entrance",
          value,
        });
      },
    },
    height: {
      get() {
        return this.$store.getters.surface(this.index).height;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "height",
          value,
        });
      },
    },
    points: {
      get() {
        return this.$store.getters.surface(this.index).points;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "points",
          value,
        });
      },
    },
    canDraw: {
      get() {
        return this.$store.getters.surface(this.index).canDraw;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "canDraw",
          value,
        });
      },
    },
    accuracy: {
      get() {
        return this.$store.getters.surface(this.index).accuracy;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "accuracy",
          value,
        });
      },
    },
    scale: {
      get() {
        return this.$store.getters.surface(this.index).scale;
      },
      set(value) {
        this.$store.commit("setSurfaceParam", {
          index: this.index,
          attribute: "scale",
          value,
        });
      },
    },
  },
  props: ["algorithms", "directions", "index", "startStep"],
  data() {
    return {
      polygon: null,
      loading: false,
      blockLength: false,
      forcedLength: 0,
    };
  },
  methods: {
    relativeMousePosition(point) {
      let rect = this.pad.getBoundingClientRect();
      return {
        x:
          this.roundToGrid(point.clientX - rect.left) +
          this.previousPointCorrection().x,
        y:
          this.roundToGrid(point.clientY - rect.top) +
          this.previousPointCorrection().y,
      };
    },
    previousPointCorrection() {
      if (this.points.length === 0) {
        return { x: 0, y: 0 };
      }
      let last = this.points.reduceRight((a) => a);
      return {
        x: last.x % this.gridSize,
        y: last.y % this.gridSize,
      };
    },
    roundToGrid(val) {
      return Math.round(val / this.gridSize) * this.gridSize;
    },
    nearestPointInPerpendicularLine(start, target) {
      if (Math.abs(start.x - target.x) > Math.abs(start.y - target.y)) {
        if (this.blockLength) {
          let side = target.x - start.x > 0 ? 1 : -1;
          return {
            x: start.x + this.forcedLength * side,
            y: start.y,
            length: this.forcedLength,
          };
        }
        return {
          x: target.x,
          y: start.y,
          length: Math.abs(start.x - target.x),
        };
      } else {
        if (this.blockLength) {
          let side = target.y - start.y > 0 ? 1 : -1;
          return {
            x: start.x,
            y: start.y + this.forcedLength * side,
            length: this.forcedLength,
          };
        }
        return {
          x: start.x,
          y: target.y,
          length: Math.abs(start.y - target.y),
        };
      }
    },
    addPoint(newPoint, redraw = false) {
      if (this.canDraw) {
        let point = redraw ? newPoint : this.relativeMousePosition(newPoint);
        if (this.points.length > 0) {
          let start = this.points[0];
          let previous = this.points[this.points.length - 1];
          point = this.nearestPointInPerpendicularLine(previous, point);
          this.drawLabel(
            (point.x + previous.x) / 2,
            (point.y + previous.y) / 2,
            point.length,
            this.helpSet
          );
          if (
            this.points.length > 2 &&
            start.x === point.x &&
            start.y === point.y
          ) {
            this.finish();
            return;
          }
        }
        let { x, y } = point;
        this.drawPoint(x, y);
        if (this.points.length > 0) {
          this.drawCircle(x, y);
        } else {
          this.drawCircle(x, y, 7, "#95B492");
        }
        this.points.push({ x, y });
        this.stopBlock();
      }
    },
    drawPoint(x, y) {
      if (this.polygon === null) {
        this.polygon = this.draw
          .polygon(x + "," + y + " " + x + "," + y)
          .stroke({ width: 1 })
          .fill("#efefef");
      } else {
        this.polygon.plot(_.concat(this.polygon.array().value, [[x, y]]));
      }
    },
    drawCircle(x, y, radius = 5, color = "#ccc") {
      this.helpSet.add(
        this.draw.circle(radius).stroke({ width: 1 }).fill(color).center(x, y)
      );
    },
    drawLabel(x, y, text, set) {
      set.add(
        this.draw
          .rect(30, 16)
          .stroke({ width: 1 })
          .fill("#efefef")
          .move(x - 15, y - 8)
      );
      set.add(
        this.draw
          .text(Math.round(this.scale * text).toString())
          .font({ size: 12 })
          .move(x - 13, y - 6)
      );
    },
    finish() {
      if (this.canDraw) {
        this.polygon.plot(this.polygon.array().value);
        this.clearSet(this.tempSet);
        this.canDraw = false;
        this.polygon.fill("#edd89d");
        if (this.valid != true) {
          this.validate();
        }
      }
    },
    finishAutomatically() {
      if (this.points.length > 2 && this.working) {
        let last = this.points.reduceRight((a) => a);
        if (last.x !== this.points[0].x && last.y !== this.points[0].y) {
          let indirectPoint =
            this.points[0].x === this.points[1].x
              ? { x: last.x, y: this.points[0].y }
              : { x: this.points[0].x, y: last.y };
          this.addPoint(indirectPoint, true);
        }
        this.addPoint(this.points[0], true);
      }
    },
    drawing(event) {
      if (this.working) {
        this.clearSet(this.tempSet);
        let target = this.relativeMousePosition(event);
        let previous = this.points[this.points.length - 1];
        let { x, y, length } = this.nearestPointInPerpendicularLine(
          previous,
          target
        );
        this.drawPoint(x, y);
        this.polygon.array().value.pop();
        this.drawLabel(x + 20, y - 20, length, this.tempSet);
      }
    },
    clearSet(set) {
      set.each(function () {
        this.remove();
      });
      set.clear();
    },
    removeLastPoint() {
      if (this.working && this.points.length > 1) {
        this.points.pop();
        this.reDraw();
      }
    },
    remove() {
      this.clear();
      this.tested = false;
      this.valid = false;
    },
    clear() {
      if (this.polygon !== null) {
        this.polygon.clear();
        this.polygon.remove();
        this.polygon = null;
      }
      this.points = [];
      this.clearSet(this.helpSet);
      this.clearSet(this.tempSet);
      this.canDraw = true;
    },
    validate() {
      this.loading = true;
      this.$http
        .post("terrace/validate", { points: this.points })
        .then(() => {
          this.valid = true;
          this.loading = false;
          this.tested = true;
        })
        .catch(() => {
          this.valid = false;
          this.loading = false;
          this.tested = true;
        });
    },
    reDraw() {
      let points = this.points.slice(0);
      let finish = !this.canDraw;
      this.clear();
      points.forEach((point) => {
        this.addPoint(point, true);
      });
      if (finish) {
        this.finish();
      }
    },
    stopBlock() {
      this.blockLength = false;
      this.forcedLength = 0;
    },
    keyevents(e) {
      if (e.key === "Enter") {
        this.finishAutomatically();
      }
      if (e.key === "Backspace") {
        this.removeLastPoint();
      }
      if (e.key === "Escape" && this.blockLength) {
        this.stopBlock();
      }
      if (
        ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(e.key) &&
        this.working
      ) {
        this.blockLength = true;
        this.forcedLength =
          10 * this.forcedLength + parseInt(e.key) / this.scale;
        let previous = this.points[this.points.length - 1];
        this.drawing({
          point: {
            clientX: previous.x,
            clientY: previous.y - this.forcedLength,
          },
        });
      }
    },
    setAvailableMaterialsForPlank(value){

        this.$store.dispatch('setAvailableMaterialsForPlank', value);

    },
  },
  mounted() {
    this.draw = SVG("drawer-pad").size("100%", "100%");
    this.helpSet = new SVG.Set();
    this.tempSet = new SVG.Set();
    this.reDraw();
    if(this.currentStep == 1){
        this.setAvailableMaterialsForPlank(this.plank);
    }
  },
  created() {
    window.addEventListener("keydown", this.keyevents);
  },
  destroyed() {
    window.removeEventListener("keydown", this.keyevents);
  },
  watch: {
    index() {
      this.reDraw();
    },
    plank: function (value) {
        this.setAvailableMaterialsForPlank(value);
    },
    currentStep(){
        if(this.currentStep == 1){
        this.setAvailableMaterialsForPlank(this.plank);
    }
    }
  },
};
</script>

<style scoped>
#drawer-pad {
  width: 700px;
  height: 700px;
  border: #464646 solid 1px;
  margin: 20px;
  background: url("/assets/grid.png");
  border-radius: 6px;
  transition: background-size 0.4s ease-in;
  -moz-transition: background-size 0.4s ease-in;
  -ms-transition: background-size 0.4s ease-in;
  -o-transition: background-size 0.4s ease-in;
  -webkit-transition: background-size 0.4s ease-in;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.draw-finish-info-snackbar {
  font-size: 20px;
}

.plank-ico-horizontal {
  margin-left: 4px;
}

.plank-ico-vertical {
  -webkit-transform: rotate(90deg);
  -moz-transform: rotate(90deg);
  -ms-transform: rotate(90deg);
  -o-transform: rotate(90deg);
  transform: rotate(90deg);
}
</style>
